import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { libraryAPI } from '../../services/api';
import { useSettings } from '../../context/SettingsContext';
import { FaCalendar, FaFilter, FaStar, FaEye } from 'react-icons/fa';
import './LibraryPage.scss';

const LibraryPage = () => {
  const { t, getLocalizedField } = useSettings();
  const [books, setBooks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [yearFrom, setYearFrom] = useState('');
  const [yearTo, setYearTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    // Delay fetch to avoid too many requests while typing
    const delayDebounceFn = setTimeout(() => {
      fetchData();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [selectedCategory, searchQuery, yearFrom, yearTo]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      if (categories.length === 0) {
        const categoriesResponse = await libraryAPI.getCategories();
        // Handle both pagination results (array is in .results) and flat array
        const categoriesData = categoriesResponse.data?.results || categoriesResponse.data || [];
        setCategories(Array.isArray(categoriesData) ? categoriesData : []);
      }

      const params = {};
      if (selectedCategory) {
        params.categories__slug = selectedCategory;
      }
      if (searchQuery) {
        params.search = searchQuery;
      }
      if (yearFrom) {
        params.publish_year__gte = yearFrom;
      }
      if (yearTo) {
        params.publish_year__lte = yearTo;
      }

      const booksResponse = await libraryAPI.getBooks(params);
      const booksData = booksResponse.data?.results || booksResponse.data || [];
      setBooks(Array.isArray(booksData) ? booksData : []);
    } catch (err) {
      console.error('Error fetching library data:', err);
      setError(t('errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="library-page">
      <div className="container">
        <div className="library-header">
          <h1 className="page-title">{t('libraryTitle')}</h1>
          <p className="page-subtitle">
            {t('librarySubtitle')}
          </p>
        </div>

        <div className="library-controls">
          <div className="search-controls">
            <div className="search-box">
              <input
                type="text"
                placeholder={t('searchBooks')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
              <span className="search-icon">🔍</span>
            </div>

            <button 
              className={`filter-toggle-btn ${showFilters ? 'active' : ''}`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <FaFilter />
            </button>
            
            <div className="view-toggle">
              <button 
                className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setViewMode('grid')}
                title={t('gridView')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 3H3V10H10V3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M21 3H14V10H21V3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M21 14H14V21H21V14Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M10 14H3V21H10V14Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <button 
                className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => setViewMode('list')}
                title={t('listView')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 6H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M8 12H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M8 18H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M3 6H3.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M3 12H3.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M3 18H3.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </div>

          <div className={`filter-panel ${showFilters ? 'show' : ''}`}>
             <div className="year-filters">
                <input 
                  type="number" 
                  placeholder={t('yearFrom')}
                  value={yearFrom}
                  onChange={(e) => setYearFrom(e.target.value)}
                  className="year-input"
                />
                <span>—</span>
                <input 
                  type="number" 
                  placeholder={t('yearTo')}
                  value={yearTo}
                  onChange={(e) => setYearTo(e.target.value)}
                  className="year-input"
                />
             </div>
          </div>

          <div className="category-filter">
            <button
              className={`category-chip ${!selectedCategory ? 'active' : ''}`}
              onClick={() => setSelectedCategory(null)}
            >
              {t('allGenres')}
            </button>
            {Array.isArray(categories) && categories.map((category) => (
              <button
                key={category.id}
                className={`category-chip ${selectedCategory === category.slug ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category.slug)}
              >
                {getLocalizedField(category, 'name')}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="loading">{t('loading')}</div>
        ) : error ? (
          <div className="error">{error}</div>
        ) : !Array.isArray(books) || books.length === 0 ? (
          <div className="empty-state">
            <p>{t('noBooks')}</p>
          </div>
        ) : (
          <div className={`books-grid ${viewMode === 'list' ? 'list-view' : ''}`}>
            {books.map((book) => (
              <Link
                to={`/library/${book.slug}`}
                key={book.id}
                className="book-card"
              >
                <div className="book-cover">
                  {book.cover ? (
                    <img src={book.cover} alt={getLocalizedField(book, 'title')} />
                  ) : (
                    <div className="no-cover">📚</div>
                  )}
                  {book.is_featured && (
                    <span className="featured-badge">{t('recommended')}</span>
                  )}
                </div>
                
                <div className="book-info">
                  <span className="book-category-label">
                     {book.categories?.[0] ? getLocalizedField(book.categories[0], 'name') : ''}
                  </span>
                  <h3 className="book-title">{getLocalizedField(book, 'title')}</h3>
                  <p className="book-authors">
                    {book.authors.map(a => a.full_name).join(', ')}
                  </p>
                  
                  <div className="book-meta">
                    <span className="book-year">
                      <FaCalendar size={12} /> {book.publish_year || '-'}
                    </span>
                    {Number(book.rating) > 0 && (
                      <span className="book-rating">
                        <FaStar size={12} /> {Number(book.rating).toFixed(1)}
                      </span>
                    )}
                    <span className="book-views">
                      <FaEye size={12} /> {book.views_count}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LibraryPage;
